import { defineStore } from "pinia";
import { getTestsForUser, createPatient, mostRecentFirst, getInstanceName, getHealthUserId } from "@/services/health/health"
import axios from "axios";
import store from "@/store/index";
import testDetails from "@/static/testDetails.json";
import { mapResult } from "./transformation";
import { ref, computed } from "vue";

const { VUE_APP_API } = process.env;

function getTestDetailsForKit(kit) {
    let details;
    details = testDetails.find((test) => test.SKU === kit.sku);
    if (!details) {
        details = testDetails.find(test => test["WP SKU"] === kit.sku);
        if (!details) {
            throw Error(`Details could not be found for ${kit.sku}`);
        }
    }
    return details;
}
function mapGeneticResult(result) {
    if (!result) {
        return false;
    }
    const [meta, results] = result;
    return {
        ...meta,
        results,
    };
}
function formatTestWithResult(test, result, kit, order) {
    const parsedResult = JSON.parse(result.data.rawJson);
    return {
        ...test,
        name: kit.testType === "bloodTest" ? getTestDetailsForKit(kit).Name : "",
        image: kit.testType === "bloodTest" ? getTestDetailsForKit(kit).Image : "",
        questionnaireAnswers: JSON.parse(test.questionnaireAnswers),
        result: kit.testType === "bloodTest" ? mapResult(parsedResult) : mapGeneticResult(parsedResult),
        kit,
        order,
        craftSlug: kit.testType === "bloodTest" ? getTestDetailsForKit(kit)["Craft Slug"] : "",
    };
}


// Function to get kit and order for a given test
async function makeHealthRequest(data) {
    //console.log('hiiiii444', localStorage.getItem("access_token"));

    try {
        return axios.request({
            url: `${VUE_APP_API}/health`,
            data: data,
            method: "post",
            headers: {
                authorization: `Bearer ${localStorage.getItem("access_token")}`
            }
        });
    } catch (error) {
        console.log('Error: ', error);
    }
}
async function getKitAndOrderForTest(barcode) {
    try {
        const { data } = await makeHealthRequest({
            method: "GET",
            path: `/kits/${barcode}`
        });
        const order = JSON.parse(data.order.rawJson);
        return { kit: data, order };
    } catch (error) {
        console.error(`Error fetching kit for barcode ${barcode}:`, error);
        return { kit: [], order: {} };
    }
}
// Function to get result for a given test
async function getResult(barcode) {
    const user = getHealthUserId();
    return await makeHealthRequest({
        method: "GET",
        path: `/reports/${barcode}/patients/${user}`
    });
}

function formatTestWithoutResult(test, kit, order) {
    return {
        ...test,
        name: kit.testType === "bloodTest" ? getTestDetailsForKit(kit).Name : "",
        image: kit.testType === "bloodTest" ? getTestDetailsForKit(kit).Image : "",
        craftSlug:
            kit.testType === "bloodTest" ? getTestDetailsForKit(kit)["Craft Slug"] : "",
        questionnaireAnswers: JSON.parse(test.questionnaireAnswers),
        result: false,
        kit,
        order,
    };
}



// Function to format test with result


// Helper function to get age group for a patient
function getAgeGroupForPatient(patient) {
    const age = new Date().getFullYear() - new Date(patient.dateOfBirth).getFullYear();
    if (age < 30) {
        return "18-29";
    }
    if (age < 40) {
        return "30-39";
    }
    if (age < 50) {
        return "40-49";
    }
    if (age < 60) {
        return "50-59";
    }
    if (age < 70) {
        return "60-69";
    }
    if (age < 80) {
        return "70-79";
    }
    if (age < 90) {
        return "80-89";
    }
    if (age < 100) {
        return "90-99";
    }
    return "over100";
}

// Define the Pinia store
export const usePatientStore = defineStore("patient", () => {

    const initialPatient = {
        userUuid: "",
        firstName: "",
        lastName: "",
        email: "",
        bioSex: "",
        dateOfBirth: "",
        appointmentInfo: "",
        tests: [],
        questionnaireAnswers: {},
        ageGroup: "",
        geneticTest: { result: { results: [], hasAnyResult: false } },
        phone: "",
        region: "",
        linkedProfile: "",
    };

    const patient = ref({ ...initialPatient });
    const mostRecentTest = ref({});
    const selectedBloodTest = ref(null);
    const blood = ref([]);
    const hasPatient = ref(false);

    function reset() {
        patient.value = { ...initialPatient };
        mostRecentTest.value = {};
    }

    /* async function checkBloodData() {
        if ( !hasPatient.value ) {
            await getPatient();
        }
        if(blood.value.length > 0){
            return true
        }
        else{
            return false
        }
    } */

    async function createPatientData() {
        return await createPatient();
    }

    function getHealthUserId() {
        let user;
        if (store.getters.profile?.permission?.type === 'OWNER') {
            const ownersList = store.getters.owners;
            if (ownersList.length > 1) {
                const matchedOwner = ownersList.find(owner => owner.barcode === store.getters.profile.profile.barcode && owner.barcode === store.getters.primaryPatient.linkedProfile);
                if (matchedOwner) {
                    console.log("Matched Owner:", matchedOwner);
                    user = store.getters.account.uuid;
                } else {
                    console.log("Owner with the barcode not found.");
                    user = store.getters.profile.profile.barcode;
                }
            } else {
                user = store.getters.account.uuid;
            }
        } else {
            user = store.getters.profile.profile.barcode;
        }
        return user;
    }

    async function getPatient() {
        const user = getHealthUserId();
        const fullPatient = await makeHealthRequest({
            path: `/patients/${user}`,
            method: "GET",
        });

        const tests = await getTestsForUser(user);
        blood.value = tests.data
        hasPatient.value = true;
        const formattedTests = (
            await Promise.all(
                tests.data.map(
                    async (test) => {
                        const { kit, order } = await getKitAndOrderForTest(test.barcode);

                        try {

                            const result = await getResult(test.barcode);

                            return formatTestWithResult(test, result, kit, order);
                        } catch (e) {
                            return formatTestWithoutResult(test, kit, order);
                        }
                    }
                )
            )
        )
            .sort(mostRecentFirst)
            .map((test, index, fullArr) => {
                return {
                    ...test,
                    instance: getInstanceName(
                        (fullArr.filter((test) => test?.kit?.testType === "bloodTest")
                            .length - index)
                    ),
                };
            });

        const formattedBloodTests = formattedTests.filter((test) => test?.kit?.testType === "bloodTest");

        const [latestTest] = formattedBloodTests;

        const geneticTest = formattedTests.find((test) => test.kit.testType === "geneticTest");

        patient.value = {
            ...fullPatient.data,
            ageGroup: getAgeGroupForPatient(fullPatient.data),
            tests: formattedBloodTests,
            questionnaireAnswers: JSON.parse(fullPatient.data.questionnaireAnswers),
            geneticTest: geneticTest ? geneticTest : false,
        };

        if (patient.value.geneticTest && patient.value.geneticTest.result) {
            patient.value.geneticTest.result.hasAnyResult =
                patient.value.geneticTest.result.results.filter(
                    (r) => !["Locked", "Awaiting Results"].includes(r.result_value)
                ).length > 0;
        }

        mostRecentTest.value = latestTest;

        return patient.value;
    }

    const nextBloodTest = computed(() => {
        if (mostRecentTest.value.result) {
            let nextTestDue;
            const lastTestDate = new Date(mostRecentTest.value.result.date);
            if (mostRecentTest.value.result.nextTestDate) {
                nextTestDue = new Date(mostRecentTest.value.result.nextTestDate);
            } else {
                nextTestDue = new Date(
                    lastTestDate.setMonth(lastTestDate.getMonth() + 3)
                );
            }

            const copy = new Date(nextTestDue);

            return {
                name: mostRecentTest.value.name,
                dueDate: nextTestDue,
                shouldOrderDate: new Date(copy.setDate(copy.getDate() - 7)),
                image: mostRecentTest.value.image,
                craftSlug: mostRecentTest.value.craftSlug,
            };
        }
        return {
            name: "Undefined",
            dueDate: new Date(),
            shouldOrderDate: new Date(),
            image: "Undefined",
            craftSlug: "Not available",
        };
    });

    function getTestByBarcode(barcode) {
        return patient.value.tests.find((test) => test.barcode === barcode);
    }

    function setSelectedBloodTest(barcode) {
        selectedBloodTest.value = getTestByBarcode(barcode);
    }

    async function editPatient(updatedFields) {

        // we only need a subset of fields from the patient, so can't use patient.value by itself
        const existingPatient = {
            userUuid: patient.value.userUuid,
            firstName: patient.value.firstName,
            lastName: patient.value.lastName,
            email: patient.value.email,
            bioSex: patient.value.bioSex,
            dateOfBirth: patient.value.dateOfBirth,
            appointmentInfo: patient.value.appointmentInfo,
            phone: patient.value.phone,
            region: patient.value.region,
            linkedProfile: patient.value.linkedProfile,
        };
        console.log('barcodee', existingPatient)

        return await makeHealthRequest({
            path: `/patients/${patient.value.userUuid}`,
            method: "PUT",
            body: { ...existingPatient, ...updatedFields },
        });
    }


    return {
        patient,
        mostRecentTest,
        selectedBloodTest,
        reset,
        createPatientData,
        getPatient,
        nextBloodTest,
        getTestByBarcode,
        editPatient,
        setSelectedBloodTest
    };
},
    { persist: true, }
);
